import { Box, Stack } from "@mui/system";
import * as React from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Sidebar from "./Sidebar";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import { styled } from "@mui/material/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Chip, CircularProgress } from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import {
  PersonOutline,
  TrendingUp,
  VerifiedUser,
  Groups,
} from "@mui/icons-material";

// --- MODERN STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
  "& .premium-row": {
    backgroundColor: "#FFF7ED",
    "&:hover": {
      backgroundColor: "#FFEDD5",
    },
  },
  "& .wrap-text": {
    whiteSpace: "normal",
  },
});

const SearchContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  "&:hover": { borderColor: "#CBD5E1" },
  width: "320px",
  display: "flex",
  alignItems: "center",
  padding: "4px 12px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const StatChip = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "12px",
  backgroundColor: `${color}15`,
  color: color,
  fontWeight: 600,
  fontSize: "0.9rem",
}));

function Customers() {
  return (
    <RootContainer>
      <Sidebar />
      <CustomerComponent />
    </RootContainer>
  );
}

const CustomerComponent = () => {
  const [customers, setCustomers] = React.useState([]);
  const [isLoading, setisLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [stats, setStats] = React.useState({
    total: 0,
    premium: 0,
    thisMonth: 0,
  });

  const columns = [
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#1E293B" }}>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "phone",
      headerName: "Contact Number",
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", color: "#64748B", fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "purchasedBrand",
      headerName: "Vehicle Brand",
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const valueFormatted = JSON.parse(params.value);
        return (
          <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", py: 1 }}>
            {valueFormatted.map((value) => (
              <Chip
                key={value}
                label={value}
                size="small"
                sx={{
                  bgcolor: "#3B82F6",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: "purchasedModel",
      headerName: "Vehicle Model",
      width: 220,
      sortable: false,
      cellClassName: "wrap-text",
      renderCell: (params) => {
        const valueFormatted = JSON.parse(params.value);
        return (
          <Stack direction="row" spacing={0.5} sx={{ overflowX: "auto", py: 1 }}>
            {valueFormatted.map((value) => (
              <Chip
                key={value}
                label={value}
                size="small"
                sx={{
                  bgcolor: "#1a5ea3",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: "salesExecutive",
      headerName: "Sales Executive",
      width: 180,
      sortable: false,
    },
    {
      field: "purchasedDate",
      headerName: "Purchase Date",
      width: 160,
      sortable: false,
      valueFormatter: (params) => {
        if (!params.value) return "N/A";
        return new Date(parseInt(params.value)).toDateString();
      },
    },
    {
      field: "customerProfile",
      headerName: "Profile Tier",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const getColor = (profile) => {
          if (profile === "Premium") return { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B" };
          if (profile === "Average") return { bg: "#DBEAFE", text: "#1E40AF", border: "#3B82F6" };
          return { bg: "#FEE2E2", text: "#991B1B", border: "#EF4444" };
        };
        const colors = getColor(params.value);
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: colors.bg,
              color: colors.text,
              border: `1.5px solid ${colors.border}`,
              fontWeight: 800,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "insuranceStatus",
      headerName: "Insurance",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "Valid" ? "success" : "error"}
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: "taxStatus",
      headerName: "Road Tax",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "Valid" ? "success" : "error"}
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: "insuranceRenewalDate",
      headerName: "Insurance Renewal",
      width: 170,
      sortable: false,
      valueFormatter: (params) => {
        if (!params.value) return "N/A";
        return new Date(parseInt(params.value)).toDateString();
      },
    },
    {
      field: "taxRenewalDate",
      headerName: "Tax Renewal",
      width: 160,
      sortable: false,
      valueFormatter: (params) => {
        if (!params.value) return "N/A";
        return new Date(parseInt(params.value)).toDateString();
      },
    },
    {
      field: "rcTransferStatus",
      headerName: "RC Transfer",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "Transferred" ? "success" : "warning"}
          variant="filled"
          sx={{ fontWeight: 700, fontSize: "0.75rem" }}
        />
      ),
    },
    {
      field: "fullPaymentRecievedDate",
      headerName: "Full Payment Date",
      width: 180,
      sortable: false,
      valueFormatter: (params) => {
        if (!params.value) return "N/A";
        return new Date(parseInt(params.value)).toDateString();
      },
    },
    { field: "email", headerName: "Email", sortable: false, width: 220 },
    { field: "gender", headerName: "Gender", sortable: false, width: 110 },
    { field: "state", headerName: "State", sortable: false, width: 160 },
    { field: "city", headerName: "City", sortable: false, width: 140 },
    { field: "address", headerName: "Address", sortable: false, width: 250 },
    { field: "pincode", headerName: "Pincode", sortable: false, width: 120 },
    {
      field: "createdAt",
      headerName: "Date Added",
      width: 160,
      sortable: false,
      valueFormatter: (params) => new Date(params.value).toDateString(),
    },
  ];

  const getAllCustomers = async () => {
    setisLoading(true);
    const roleID = localStorage.getItem("roleId");
    const token = localStorage.getItem("accessToken");
    const salesExecutive = localStorage.getItem("fullName");
    const config = { headers: { auth: token } };

    try {
      const endpoint =
        roleID !== "af7c1fe6-d669-414e-b066-e9733f0de7a8"
          ? `/salesCustomer/${salesExecutive}`
          : "/customers";
      const response = await axios.get(endpoint, config);
      const data = response.data.data;
      setCustomers(data);

      // Calculate stats
      const total = data.length;
      const premium = data.filter((c) => c.customerProfile === "Premium").length;
      const currentMonth = new Date().getMonth();
      const thisMonth = data.filter((c) => {
        const createdMonth = new Date(c.createdAt).getMonth();
        return createdMonth === currentMonth;
      }).length;

      setStats({ total, premium, thisMonth });
    } catch (e) {
      console.error(e);
      toast.error("Failed to load customers");
    }
    setisLoading(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredCustomers = React.useMemo(() => {
    if (!searchQuery) return customers;
    return customers.filter(
      (customer) =>
        customer.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.phone?.includes(searchQuery) ||
        customer.salesExecutive?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [customers, searchQuery]);

  React.useEffect(() => {
    getAllCustomers();
  }, []);

  return (
    <ContentArea>
      {/* HEADER SECTION */}
      <Stack 
        direction={{ xs: "column", md: "row" }} 
        justifyContent="space-between" 
        alignItems={{ xs: "flex-start", md: "center" }} 
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2rem", md: "3rem" }
            }}
          >
            Customer Portfolio
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
            Complete overview of converted customers and their vehicle ownership details
          </Typography>
        </Box>

        <SearchContainer>
          <SearchIcon sx={{ color: "#94A3B8", fontSize: "20px", mr: 1 }} />
          <InputBase
            placeholder="Search customers..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, fontSize: "14px" }}
          />
        </SearchContainer>
      </Stack>

      {/* STATS SECTION */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        sx={{ mb: 4, flexWrap: "wrap" }}
      >
        <StatChip color="#3B82F6">
          <Groups fontSize="small" />
          Total Customers: {stats.total}
        </StatChip>

        <StatChip color="#F59E0B">
          <VerifiedUser fontSize="small" />
          Premium Clients: {stats.premium}
        </StatChip>

        <StatChip color="#10B981">
          <TrendingUp fontSize="small" />
          Added This Month: {stats.thisMonth}
        </StatChip>
      </Stack>

      {/* DATA GRID SECTION */}
      <GlassCard elevation={0}>
        <Box sx={{ height: "65vh", width: "100%" }}>
          <StyledDataGrid
            rows={filteredCustomers}
            columns={columns}
            getRowId={(row) => row.phone}
            loading={isLoading}
            disableSelectionOnClick
            checkboxSelection
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            getRowClassName={(params) =>
              params.row.customerProfile === "Premium" ? "premium-row" : ""
            }
            sortModel={[{ field: "createdAt", sort: "desc" }]}
            sx={{
              "& .MuiDataGrid-toolbarContainer": {
                padding: "16px",
                gap: "12px",
                borderBottom: "1px solid #E2E8F0",
                "& .MuiButton-root": {
                  color: "#64748B",
                  fontWeight: 700,
                  "&:hover": {
                    color: "#1E293B",
                  },
                },
              },
            }}
          />
        </Box>

        {/* Summary Footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 3, sm: 0 },
            pt: 3,
            mt: 2,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600 }}>
            Showing {filteredCustomers.length} of {stats.total} customers
          </Typography>
          <Stack direction="row" spacing={3} sx={{ alignSelf: { xs: "center", sm: "auto" } }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                Valid Insurance
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#10B981" }}>
                {customers.filter((c) => c.insuranceStatus === "Valid").length}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                Valid Tax
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#10B981" }}>
                {customers.filter((c) => c.taxStatus === "Valid").length}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                RC Transferred
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#3B82F6" }}>
                {customers.filter((c) => c.rcTransferStatus === "Transferred").length}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </GlassCard>

      <ToastContainer position="bottom-right" />
    </ContentArea>
  );
};

export default Customers;